import React from 'react';
import styled from 'styled-components';

interface Material {
  id: number;
  name: string;
  type: string;
}

const StreamingMaterials: React.FC = () => {
  const materials: Material[] = [
    { id: 1, name: '1강_유방영상의학 개론.pdf', type: 'PDF' },
    { id: 2, name: '2강_유방촬영술 기본원리.pptx', type: 'PPT' },
    { id: 3, name: '3강_디지털 유방촬영술.pdf', type: 'PDF' },
    { id: 4, name: '4강_유방 MRI 기본개념.pdf', type: 'PDF' },
  ];

  // 강의자료가 없는 경우 빈 상태 표시
  if (materials.length === 0) {
    return (
      <EmptyState>
        <EmptyStateTitle>강의자료를 찾을 수 없습니다.</EmptyStateTitle>
        <EmptyStateDescription>
          본 강의는 별도의 강의자료가<br/>
          제공되지 않는 강의입니다.
        </EmptyStateDescription>
      </EmptyState>
    );
  }

  return (
    <MaterialsContainer>
      <MaterialsList>
        {materials.map((material) => (
          <MaterialItem key={material.id}>
            <MaterialContent>
              <IconSection>
                <IconWrapper>
                  <DocumentIcon />
                </IconWrapper>
              </IconSection>
              <ContentSection>
                <MainContent>
                  <MaterialTitle>{material.name}</MaterialTitle>
                </MainContent>
                <TypeSection>
                  <MaterialType>{material.type}</MaterialType>
                </TypeSection>
              </ContentSection>
            </MaterialContent>
          </MaterialItem>
        ))}
      </MaterialsList>
    </MaterialsContainer>
  );
};

const MaterialsContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  gap: 12px;
`;

const MaterialsList = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  gap: 10px;
`;

const MaterialItem = styled.div`
  width: 100%;
  border-radius: 12px;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;
  
  &:hover {
    background: rgba(112, 115, 124, 0.04);
  }
  
  &:focus {
    background: rgba(112, 115, 124, 0.04);
    outline: none;
  }
  
  &:active {
    background: rgba(112, 115, 124, 0.08);
  }
`;

const MaterialContent = styled.div`
  flex: 1;
  width: 100%;
  height: 72px;
  padding: 12px 16px;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 8px;
  position: relative;
`;

const IconSection = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  overflow: hidden;
`;

const IconWrapper = styled.div`
  padding-right: 8px;
  display: flex;
  justify-content: flex-start;
  align-items: center;
`;

const DocumentIcon = styled.div`
  width: 48px;
  height: 48px;
  padding: 8px;
  background: var(--To-Be-Doctor-99, #F0F7F7);
  overflow: hidden;
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  position: relative;
  
  &::after {
    content: '';
    width: 32px;
    height: 32px;
    position: absolute;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
    background-image: url("data:image/svg+xml,%3Csvg width='32' height='32' viewBox='0 0 32 32' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M16.8671 2.80029C16.8613 2.80031 16.8555 2.80032 16.8498 2.80033L16.7829 2.80043L10.5527 2.80043C9.84624 2.80041 9.24467 2.8004 8.75076 2.84075C8.23188 2.88315 7.72709 2.97599 7.24451 3.22188C6.51696 3.59259 5.92543 4.18411 5.55472 4.91167C5.30884 5.39425 5.21599 5.89904 5.1736 6.41792C5.13325 6.91182 5.13326 7.51337 5.13328 8.21984V23.781C5.13326 24.4874 5.13325 25.0891 5.1736 25.5829C5.21599 26.1018 5.30884 26.6066 5.55472 27.0892C5.92543 27.8168 6.51696 28.4083 7.24451 28.779C7.72709 29.0249 8.23188 29.1177 8.75076 29.1601C9.24465 29.2005 9.84621 29.2005 10.5527 29.2004H21.4471C22.1536 29.2005 22.7552 29.2005 23.2491 29.1601C23.768 29.1177 24.2727 29.0249 24.7553 28.779C25.4829 28.4083 26.0744 27.8168 26.4451 27.0892C26.691 26.6066 26.7838 26.1018 26.8262 25.5829C26.8666 25.0891 26.8666 24.4875 26.8665 23.781V13.1336L20.0956 13.1336C19.7551 13.1336 19.4349 13.1337 19.1657 13.1117C18.8733 13.0878 18.5429 13.0324 18.2139 12.8648C17.7498 12.6283 17.3724 12.2509 17.1359 11.7868C16.9683 11.4578 16.9129 11.1274 16.889 10.835C16.867 10.5658 16.867 10.2456 16.8671 9.90521L16.8671 2.80029Z' fill='%23678B8C'/%3E%3Cpath d='M19.1338 3.77057L19.1398 3.77664L26.2204 10.8675H19.3338C19.2233 10.8675 19.1338 10.7779 19.1338 10.6675V3.77057Z' fill='%23678B8C'/%3E%3C/svg%3E");
    background-size: contain;
    background-repeat: no-repeat;
    background-position: center;
  }
`;

const ContentSection = styled.div`
  flex: 1;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 8px;
`;

const MainContent = styled.div`
  flex: 1;
  display: flex;
  justify-content: flex-start;
  align-items: center;
`;

const MaterialTitle = styled.div`
  flex: 1;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  gap: 4px;
  
  min-height: 24px;
  color: var(--Label-Normal, #171719);
  font-size: 16px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 500;
  line-height: 24px;
  letter-spacing: 0.09px;
  word-wrap: break-word;
`;

const TypeSection = styled.div`
  overflow: hidden;
  display: flex;
  justify-content: flex-end;
  align-items: center;
`;

const MaterialType = styled.div`
  padding-left: 8px;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  
  text-align: right;
  color: var(--Label-Alternative, rgba(55, 56, 60, 0.61));
  font-size: 16px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 400;
  line-height: 24px;
  letter-spacing: 0.09px;
  word-wrap: break-word;
`;

const EmptyState = styled.div`
  width: 100%;
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  padding: 160px 0;
  gap: 24px;
`;

const EmptyStateTitle = styled.div`
  text-align: center;
  color: var(--Label-Normal, #171719);
  font-size: 20px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 600;
  line-height: 28px;
  word-wrap: break-word;
`;

const EmptyStateDescription = styled.div`
  text-align: center;
  color: var(--Label-Alternative, rgba(55, 56, 60, 0.61));
  font-size: 16px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 400;
  line-height: 26px;
  letter-spacing: 0.09px;
  word-wrap: break-word;
`;

export default StreamingMaterials;
